import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { EmergencyAction, EmergencyGetter } from '@/store/emergency/emergency.module';
import { EmergencyAttachmentSchema } from '@/store/emergency/emergency.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'EmergencyAttachmentFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    emergencyId: {
      type: Number,
    },
  },
  emits: [
    'hidden'
  ],
  setup: (props, context) => {
    const store = useStore();
    const attachmentForm = ref<InstanceType<typeof BaseModalComponent>>();
    const fileDialog = ref<HTMLInputElement>();

    // form
    interface Form {
      emergencyId?: number | null;
      attachmentTypeId: number;
      name?: string | null;
      file?: string | null;
    }
    const formData = reactive<Form>({
      emergencyId: props.emergencyId,
      attachmentTypeId: 1,
      name: '',
      file: ''
    });
    const validator = computed(() => EmergencyAttachmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      file: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'file'))
    });
    const isValid = computed(() => !errors.name && !errors.file);
    const isLoading = computed<boolean>(() => store.getters[EmergencyGetter.STATUS] === 'init' || store.getters[EmergencyGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      Promise.resolve(formData.emergencyId = props.emergencyId)
        .then(() => attachmentForm.value?.show());
    };

    // hide modal
    const hide = () => {
      attachmentForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.attachmentTypeId = 1;
        formData.name = '';
        formData.file = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const files = fileDialog.value?.files as FileList;
        store.dispatch(EmergencyAction.ADD_ATTACHMENTS, {
          id: formData.emergencyId,
          attachmentTypeId: formData.attachmentTypeId,
          names: [formData.name],
          attachments: files,
        }).then(() => hide());
      }
    };

    const fileDialogChange = () => {
      const files = fileDialog.value?.files as FileList;
      const file = files.item(0) as File;
      formData.file = file.name;
      unsubmit();
    };

    const handleHidden = () => {
      context.emit('hidden', props.emergencyId);
    };

    return {
      formData,
      attachmentForm,
      fileDialog,
      isLoading,
      errors,
      submited,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      fileDialogChange,
      handleHidden,
    };
  },
});
